import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Schema } from "effect";

import { Sidebar, SidebarProvider, SidebarRail } from "~/components/ui/sidebar";
import { getLocalStorageItem } from "~/hooks/useLocalStorage";
import { Sheet, SheetPopup } from "./ui/sheet";

const RIGHT_SIDEBAR_WIDTH_STORAGE_KEY = "chat_right_sidebar_codex_width_v4";
import {
  RIGHT_PANEL_RESERVED_WIDTH_VAR,
  resolveRightPanelColumnOffsets,
} from "./threeColumnLayout";
import {
  resolveInitialRightPanelWidth,
  resolveRightPanelMaxWidth,
  RIGHT_SIDEBAR_DEFAULT_WIDTH,
  RIGHT_SIDEBAR_MIN_WIDTH,
} from "./rightPanelWidth";
import { useViewportWidth } from "~/hooks/useViewportWidth";
import { resolveRightPanelInlineWidth } from "./rightPanelLayout";
import {
  composerFitsAtSidebarWidth,
  readContentWidth,
  readFooterGap,
  resolveMinimumComposerWidth,
  type ComposerFitMeasurements,
} from "./inlineSidebarWidthFit";
const COMPOSER_COMPACT_MIN_LEFT_CONTROLS_WIDTH_PX = 208;
const RECOVERABLE_BROWSER_TITLE_PATTERN = /\b(?:502|bad gateway)\b/iu;

export function publishRightPanelReservedWidth(width: string | null): void {
  const root = document.documentElement;
  if (width === null) {
    root.style.removeProperty(RIGHT_PANEL_RESERVED_WIDTH_VAR);
    return;
  }
  root.style.setProperty(RIGHT_PANEL_RESERVED_WIDTH_VAR, width);
}

/**
 * The panel's opening width in px, clamped against the room available *now*.
 *
 * This used to re-clamp against its own copy of the constant 720px ceiling,
 * independently of the drag clamp. Two clamps for one bound is how a drag could
 * widen the panel and a reload silently take it back, so both now come from
 * `resolveRightPanelMaxWidth` and there is no second number to disagree with.
 */
export function resolvePersistedRightPanelWidth(input: {
  readonly storageKey: string;
  readonly defaultWidth: number;
  readonly viewportWidth: number;
  readonly rowWidth?: number | undefined;
}): number {
  const storedWidth =
    typeof window === "undefined" ? null : getLocalStorageItem(input.storageKey, Schema.Finite);
  return resolveInitialRightPanelWidth(
    storedWidth,
    input.viewportWidth,
    input.rowWidth,
    input.defaultWidth,
  );
}

/** Reads the live geometry `composerFitsAtSidebarWidth` decides from. */
function measureComposerFit(
  composerForm: HTMLElement,
  composerViewport: HTMLElement,
): ComposerFitMeasurements {
  const viewportStyle = window.getComputedStyle(composerViewport);
  const rightActions = composerForm.querySelector<HTMLElement>(
    "[data-chat-composer-actions='right']",
  );
  const footer = composerForm.querySelector<HTMLElement>("[data-chat-composer-footer='true']");

  return {
    formWidth: composerForm.getBoundingClientRect().width,
    viewportContentWidth: readContentWidth({
      clientWidth: composerViewport.clientWidth,
      paddingLeft: viewportStyle.paddingLeft,
      paddingRight: viewportStyle.paddingRight,
    }),
    composerClientWidth: composerForm.clientWidth,
    composerScrollWidth: composerForm.scrollWidth,
    minimumComposerWidth: resolveMinimumComposerWidth({
      leftControlsWidth: COMPOSER_COMPACT_MIN_LEFT_CONTROLS_WIDTH_PX,
      rightActionsWidth: rightActions?.getBoundingClientRect().width ?? 0,
      footerGap: readFooterGap(footer ? window.getComputedStyle(footer) : null),
    }),
  };
}

export function deriveBrowserSidebarLabel(input: {
  readonly title?: string | null;
  readonly url?: string | null;
}): string {
  const title = input.title?.trim();
  if (title && !RECOVERABLE_BROWSER_TITLE_PATTERN.test(title)) {
    return title;
  }
  const url = input.url?.trim();
  if (!url) {
    return "Browser";
  }
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/api/dev-tunnel/")) {
      return "Browser";
    }
    return parsed.hostname || "Browser";
  } catch {
    return url;
  }
}

/**
 * Inline-sidebar wrapper for the unified right panel. Owns the
 * `SidebarProvider` + resize behaviour; the contained RightSidebarShell owns
 * the tab strip, close button, and active-pane content slot.
 */
export function RightPanelInlineSidebar(props: {
  readonly open: boolean;
  readonly maximized?: boolean;
  readonly defaultWidth?: number;
  readonly storageKey?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  const { open, maximized = false, onClose, children } = props;
  const columnRef = useRef<HTMLDivElement | null>(null);
  const columnOffsets = resolveRightPanelColumnOffsets({ presentation: "inline", open });
  const storageKey = props.storageKey ?? RIGHT_SIDEBAR_WIDTH_STORAGE_KEY;

  // The two measurements the panel's ceiling is derived from. Subscribed
  // rather than read once: the ceiling has to fall when the window shrinks or
  // the left sidebar is dragged wider, and a snapshot taken at mount never
  // would. This is what replaced the constant 720px cap.
  const viewportWidth = useViewportWidth();
  const rowWidth = useContentRowWidth(columnRef, open && !maximized);
  const maxWidth = resolveRightPanelMaxWidth(viewportWidth, rowWidth);

  const inlineWidth = resolveRightPanelInlineWidth({
    maximized,
    defaultWidth: `${resolvePersistedRightPanelWidth({
      storageKey,
      defaultWidth: props.defaultWidth ?? RIGHT_SIDEBAR_DEFAULT_WIDTH,
      viewportWidth,
      rowWidth,
    })}px`,
  });

  // Published on the document because the bar is an ancestor *sibling*, not a
  // descendant — it cannot read this provider's own `--sidebar-width`. The bar
  // reserves this width so it stops where the panel's column begins, instead of
  // painting across the panel's own tab strip (the bar is z-20, the panel z-10).
  //
  // *Measured*, not restated. The panel's width has five writers — this
  // component's `style`, the rail's hydration effect, the drag handler, the
  // composer-fit probe, and CSS itself while the column transitions — and the
  // bar used to be told the number separately by whichever of them remembered
  // to. Each fix taught one more writer to publish and the next writer added
  // broke the seam again. Observing the column's own box makes the agreement
  // structural: there is one number, and it is the one the user can see.
  //
  // The element observed is the provider wrapper, not the panel itself, because
  // the wrapper is the panel's *in-flow* footprint in the content row — which is
  // exactly what the bar has to give up. It is 0 while the panel is parked
  // offcanvas and 0 below `md`, where `Sidebar` renders a sheet and takes no
  // column at all, so both of those need no special case here.
  useEffect(() => {
    const column = columnRef.current;
    if (column === null || !open) {
      publishRightPanelReservedWidth(null);
      return;
    }
    if (maximized) {
      // The panel has taken the window; there is no seam left to hold, and the
      // wrapper's own width no longer describes it (the column is `100%` of the
      // viewport, not of this flex item).
      publishRightPanelReservedWidth("100%");
      return () => {
        publishRightPanelReservedWidth(null);
      };
    }
    const publish = () =>
      publishRightPanelReservedWidth(`${column.getBoundingClientRect().width}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(column);
    return () => {
      observer.disconnect();
      publishRightPanelReservedWidth(null);
    };
  }, [maximized, open]);
  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );
  const shouldAcceptInlineSidebarWidth = useCallback(
    ({ nextWidth, wrapper }: { nextWidth: number; wrapper: HTMLElement }) => {
      const composerForm = document.querySelector<HTMLElement>("[data-chat-composer-form='true']");
      const composerViewport = composerForm?.parentElement;
      if (!composerForm || !composerViewport) return true;

      // Measured with the candidate width applied, then put back. The composer
      // only reflows once the variable is set, so asking whether a width fits
      // means briefly living at it.
      const previousSidebarWidth = wrapper.style.getPropertyValue("--sidebar-width");
      wrapper.style.setProperty("--sidebar-width", `${nextWidth}px`);
      const measurements = measureComposerFit(composerForm, composerViewport);
      if (previousSidebarWidth.length > 0) {
        wrapper.style.setProperty("--sidebar-width", previousSidebarWidth);
      } else {
        wrapper.style.removeProperty("--sidebar-width");
      }

      return composerFitsAtSidebarWidth(measurements);
    },
    [],
  );

  return (
    <SidebarProvider
      defaultOpen={false}
      open={open}
      onOpenChange={onOpenChange}
      ref={columnRef}
      className="w-auto min-h-0 flex-none bg-transparent"
      style={
        {
          "--forge-sidebar-top": columnOffsets.top,
          "--sidebar-width": inlineWidth.width,
        } as CSSProperties
      }
    >
      <Sidebar
        side="right"
        collapsible="offcanvas"
        className="border-l border-border bg-card text-foreground"
        {...(inlineWidth.resizable
          ? {
              resizable: {
                maxWidth,
                minWidth: RIGHT_SIDEBAR_MIN_WIDTH,
                shouldAcceptWidth: shouldAcceptInlineSidebarWidth,
                storageKey,
              },
            }
          : {})}
      >
        {children}
        {/* No rail while maximized: the panel already fills the column, so a
            drag has nowhere to go — and a live handle lets one silently
            un-maximize while the header control still reads "Restore". */}
        {inlineWidth.resizable ? <SidebarRail /> : null}
      </Sidebar>
    </SidebarProvider>
  );
}

/**
 * The width of the flex row the panel shares with the transcript.
 *
 * The *row* is observed, not the panel, because the number the ceiling needs is
 * how much space the two columns are dividing — which is unchanged by the panel
 * growing, so there is no feedback loop between this measurement and the width
 * it bounds. Widening the left sidebar does shrink it, which is exactly how the
 * transcript keeps its minimum without the left sidebar's own guard having to
 * know the panel exists.
 *
 * `useLayoutEffect` so the first paint is already clamped: measuring one
 * observer tick later would flash an over-wide panel on every mount whenever
 * the stored width no longer fits.
 *
 * `undefined` while disabled or unmeasured; `resolveRightPanelMaxWidth` falls
 * back to the viewport fraction, which is looser but never unsafe.
 */
function useContentRowWidth(
  columnRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
): number | undefined {
  const [rowWidth, setRowWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!enabled) {
      setRowWidth(undefined);
      return;
    }
    const row = columnRef.current?.parentElement;
    if (!row) return;

    // clientWidth is integral, so sub-pixel row jitter bails out of setState
    // rather than re-rendering the whole panel every frame of a window drag.
    const measure = () => setRowWidth(row.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [columnRef, enabled]);

  return rowWidth;
}

/**
 * Sheet wrapper used on narrow viewports - same chrome story as the inline
 * sidebar, just rendered as an overlay sheet.
 */
export function RightPanelSheet(props: {
  readonly open: boolean;
  readonly fullscreen: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  return (
    <Sheet
      open={props.open}
      onOpenChange={(next) => {
        if (!next) {
          props.onClose();
        }
      }}
    >
      <SheetPopup
        side="right"
        showCloseButton={false}
        keepMounted
        backdropClassName={props.fullscreen ? "top-[var(--forge-app-top-bar-height)]" : undefined}
        viewportClassName={
          props.fullscreen
            ? "top-[var(--forge-app-top-bar-height)] min-h-0 overflow-hidden"
            : undefined
        }
        className={
          props.fullscreen
            ? "h-full min-h-0 w-screen max-w-none overflow-hidden border-l-0 p-0"
            : "w-[min(520px,calc(100vw-2rem))] max-w-[520px] p-0"
        }
      >
        {props.children}
      </SheetPopup>
    </Sheet>
  );
}
